import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { fetchCompanyVAPTDimensions as fetchCompanyDimensionsService} from 'services/modules/vapt/data';
import { uploadVulnerability as uploadVulnerabilityService, fetchReportsQuery as fetchReportsQueryService } from 'services/modules/vapt/report';
import { useNotification } from 'config/Notification';
import LoadingOverlay from 'components/LoadingOverlay.jsx';

import {
  Card,
  CardHeader,
  Divider,
  Button,
  CardContent,
  Checkbox,
  FormHelperText,
  FormControlLabel,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TextField,
  IconButton,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';

import Editor from './Editor';
import UploadedVulnerabilities from './UploadedVulnerabilities';

function UploadVulnerabilities() {
  // use location hook
  const location = useLocation();
  // Notification
  const {openNotification} = useNotification();
  // search params and variables
  const searchParams = new URLSearchParams(location.search);
  const company = searchParams.get('company');
  const reportId = searchParams.get('report');
  const category = searchParams.get('category')
  const subcategory = searchParams.get('subcategory')
  
  // State variables
  const [dimensions, setDimensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [report,setReport]=useState({})
  const [reportDate,setReportDate]=useState(null);
  const [subDimensions, setSubDimensions] = useState([]);
  const [selectedDimension, setSelectedDimension] = useState(null);
  const [selectedSubDimension, setSelectedSubDimension] = useState(null);
  const severities = ["Informational","Low", "Medium", "High", "Critical"];
  const [formErrors, setFormErrors] = useState({});
  const [scoreError, setScoreError] = useState('');
  const [checked,setChecked] = useState(false);
  const [validSeverities, setValidSeverities] = useState(severities);
  const [additionalFieldsEnabled, setAdditionalFieldsEnabled] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    severity: '',
    score: '',
    impact:'',
    remediation: '',
    ThreatActor:'',
    Tactics:'',
    ThreatActorDescription:''
  });
  const [dynamicFields, setDynamicFields] = useState([]);
  const [evidence,setEvidence] = useState('')

  useEffect(()=>{
    if (reportId !== null)
      fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[reportDate])
  useEffect(() => {
    if (company !== null && report?.domain!==null) 
      fetchCompanyDimensionsData(company,category);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const determineValidSeverities = (score) => {
    const numericScore = Number(score)
    if (score === "") return ["Informational","Low","Medium","High","Critical"]
    if (numericScore === 0) return["Informational"]
    if (numericScore >=1 && numericScore <= 4) return ["Low"];
    if (numericScore > 4 && numericScore <= 6) return ["Medium"];
    if (numericScore > 6 && numericScore <= 8) return ["High"];
    if (numericScore > 8 && numericScore <= 10) return ["Critical"];
    if (numericScore >10 || numericScore<0) return [];
    return []
  };
  
  // Determines the valid scores based on severities selected by user
  const determineValidScores = (severity) => {
    switch (severity) {
      case "Informational": return 0;
      case "Low": return 3;
      case "Medium": return 5;
      case "High": return 7;
      case "Critical": return 9;
      default: return 0;
    }
  };
  
  // Handles form validation errors
  const handleErrors = (fieldName) => {
    setFormErrors((prevformErrors) => ({
      ...prevformErrors,
      [fieldName]: true,
    }));
  };
  
  // Fetch company dimesnions data based on company id and category
  const fetchCompanyDimensionsData = async (company,category) => {
    setLoading(true);
    const params={company:company}
    let response = await fetchCompanyDimensionsService({ params });
    let subcategories = response?.find(cat=>cat._id === category)?.subcategories
    let dimensionLabels=subcategories?.find(cat=>cat.category===subcategory)?.Dimensions
    let activeDimensions=dimensionLabels?.filter(dim=>dim.status===true)
    setDimensions(activeDimensions);
    setLoading(false);
  };
  
  // Fetch existing reports based on report id
  const fetchReportData = async () => {
    setLoading(true);
    const response =await fetchReportsQueryService({ reportId });
    setReport(response);
    setReportDate(response?.createdAt)
    setLoading(false);
  };

  // Handles changes in dimension dropdown
  const handleDimensionChange = (event) => {
    const dimensionLabel = event.target.value;
    const selectedDimensionObj = dimensions.find((dim) => dim.label === dimensionLabel);
    setSelectedDimension(selectedDimensionObj);

    // Find the selected dimension object
    if (selectedDimensionObj) {
      setSubDimensions(selectedDimensionObj.subDimensions || []);
    }
  };

  // Handles changes in sub-dimension dropdown
  const handleSubDimensionChange = (event) => {
    const selectedSubDimensionObj = subDimensions.find((subDim) => subDim.label === event.target.value);
    setSelectedSubDimension(selectedSubDimensionObj);
    setAdditionalFieldsEnabled(true);
  };

  // Handles changes in additional fields
  const handleAdditionalFieldChange = (e) => {
    const { name, value } = e.target;
    if (name === "score") {
      const scoreValue = parseInt(value);
      if (isNaN(scoreValue) || scoreValue < 0 || scoreValue > 10) {
        setScoreError("Score must be a number between 0 and 10");
        const validSeverities = determineValidSeverities(value);
        setValidSeverities(validSeverities);
      } else {
        setScoreError("");
        const validSeverities = determineValidSeverities(value);
        setValidSeverities(validSeverities);
        if (!validSeverities.includes(formData.severity)) 
          setFormData({ ...formData, severity: '' });
      }
    }
    if (name === "severity") {
      const validScores = determineValidScores(value);
      setFormData({ ...formData, score: validScores, severity: value });
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handles form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const updatedFormData={...formData};
    updatedFormData.score = determineValidScores(updatedFormData.severity);
    updatedFormData.report=report?.id;
    updatedFormData.dimension=selectedSubDimension?.id;
    updatedFormData.evidence=evidence;
    if(!checked)
    updatedFormData.title=updatedFormData.title.split(" ").map(word=>word.charAt(0).toUpperCase()+word.slice(1).toLowerCase()).join(' ');

    const dynamicFieldData = dynamicFields.reduce((acc, field) => {
      // Check if fieldName is ThreatActor or Tactics
      if (field.fieldName === "ThreatActor" || field.fieldName === "Tactics") {
        acc[field.fieldName] = field.fieldValue
          .replace(/,/g, "\n")         // Replace commas with newlines
          .replace(/\n\s+/g, "\n");    // Remove whitespace after each newline
      } else {
        acc[field.fieldName] = field.fieldValue;
      }
      return acc;
    }, {});

  if(updatedFormData["ThreatActor"] ||  updatedFormData["Tactics"] || updatedFormData["ThreatActorDescription"]){
      dynamicFieldData.ThreatActor = updatedFormData["ThreatActor"];
      dynamicFieldData.Tactics = updatedFormData["Tactics"];
      dynamicFieldData.ThreatActorDescription = updatedFormData["ThreatActorDescription"]
  }
    const submissionData = {
      ...updatedFormData,
      ...dynamicFields.reduce((acc, field) => {
        acc[field.fieldName] = field.fieldValue;
        return acc;
      }, {})
    };
    delete updatedFormData.Tactics;
    delete updatedFormData.ThreatActor;
    delete updatedFormData.ThreatActorDescription;
    const payload = {
      ...updatedFormData,
      data: dynamicFieldData,
    };
    let payloadSize = new TextEncoder().encode(JSON.stringify(payload)).length;
    if(payloadSize>5242880)
      {
        openNotification("Size is too large")
        return;
      }
      const emptyFields = Object.entries(submissionData)
      .filter(([name, value]) => value === '')
      .map(([name]) => name);

    if (emptyFields.length > 0) {
      const fieldNames = emptyFields.join(', ');
      openNotification(`Please fill in the required fields: ${fieldNames}`,'error');
      return;
    }
    setLoading(true);
    const response = await uploadVulnerabilityService(payload);
    if (response==="success") {
      openNotification('Vulnerability uploaded successfully','success');
      window.location.reload();
    } else {
      openNotification('Failed to upload vulnerability:','error');
    }
    setLoading(false);
  };

  // Handles adding a new dynamic field
  const handleAddField = () => {
    setDynamicFields([...dynamicFields, { fieldName: '', fieldValue: '' }]);
  };

  // Handles changing a dynamic field
  const handleDynamicFieldChange = (index, fieldKey, value) => {
    const updatedFields = [...dynamicFields];
    updatedFields[index][fieldKey] = value;
    setDynamicFields(updatedFields);
  };

  // Handles removing a dynamic field
  const handleRemoveField = (index) => {
    const updatedFields = [...dynamicFields];
    updatedFields.splice(index, 1);
    setDynamicFields(updatedFields);
  };

  return (
    <div>
      <Card className="card">
        <CardHeader
          title={
            <React.Fragment>
              <div className="card-head">
                <div className="flex-head">
                  <label className="label">
                    <UploadIcon className="upload-icon" />
                    Upload Vulnerabilities
                  </label>
                </div>
                <div>
                  <Button variant="contained" className="back font" onClick={() => window.history.back()}>
                    Back
                  </Button>
                </div>
              </div>
            </React.Fragment>
          }
        />
        <Divider className="divider" />
        {loading &&  
          <div align="center">
           <LoadingOverlay />
          </div>
        }
        <CardContent>
          <div className="row">
            <div className="col-lg-8 col-md-8 col-sm-12 col-xs-12">
          <Card className="vulnerability-card row company-screen-bg">
            <div className="row col-lg-12">
              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md">
                <FormControl
                  fullWidth
                  onBlur={() => setFormErrors({ ...formErrors, dimension: !selectedDimension })}
                  error={!selectedDimension && formErrors.dimension}
                >
                  <InputLabel htmlFor="dimension">Dimension</InputLabel>
                  <Select
                    value={selectedDimension?selectedDimension.label:''}
                    onChange={handleDimensionChange}
                    label="Dimension"
                  >
                    {dimensions?.map((dimension) => (
                      <MenuItem key={dimension.id} value={dimension.label}>
                        {dimension.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.dimension && (
                    <FormHelperText error>
                      Dimension is required
                    </FormHelperText>
                  )}
                </FormControl>
              </div>
              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md">
                <FormControl fullWidth>
                  <InputLabel htmlFor="subDimension">Sub-Dimension</InputLabel>
                  <Select
                    value={selectedSubDimension?selectedSubDimension.label:''}
                    onChange={handleSubDimensionChange}
                    label="Sub-Dimension"
                  >
                    {subDimensions.map((subDimension) => (
                      <MenuItem key={subDimension.id} value={subDimension.label}>
                        {subDimension.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              {additionalFieldsEnabled && (
                <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md">
                  <FormControl fullWidth>
                    <TextField
                      name="title"
                      label="Title"
                      value={formData.title}
                      onChange={handleAdditionalFieldChange}
                      onBlur={() => handleErrors('title')}
                      error={formData.title === '' && formErrors.title}
                      helperText={formData.title === '' && formErrors.title ? 'Field is required' : ''}
                    />
                  </FormControl>
                  <FormControlLabel
                   className='mx-1'
                   control={<Checkbox checked={checked} onChange={()=>setChecked(!checked)} />}
                   label="Preserve Title Format"
                   />
                </div>
              )}
              {additionalFieldsEnabled && (
                <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md">
                  <FormControl fullWidth>
                    <TextField
                      multiline
                      maxRows={4}
                      name="description"
                      label="Description"
                      value={formData.description}
                      onChange={handleAdditionalFieldChange}
                      onBlur={() => handleErrors('description')}
                      error={formData.description === '' && formErrors.description}
                      helperText={formData.description === '' && formErrors.description ? 'Field is required' : ''}
                    />
                  </FormControl>
                </div>
              )}
              {additionalFieldsEnabled && (
                <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md">
                  <FormControl fullWidth>
                    <InputLabel htmlFor="severity">Severity</InputLabel>
                    <Select
                      name="severity"
                      value={formData.severity}
                      onChange={handleAdditionalFieldChange}
                      label="Severity"
                    >
                      {validSeverities.map((severity, index) => (
                        <MenuItem key={index} value={severity}>
                          {severity}
                        </MenuItem>
                      ))}
                    </Select>
                    {formData.severity === '' && formErrors.severity && (
                      <FormHelperText>Field is required</FormHelperText>
                    )}
                  </FormControl>
                </div>
              )}
              {additionalFieldsEnabled && (
                <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md">
                  <FormControl fullWidth>
                    <TextField
                      name="score"
                      label="Score"
                      value={determineValidScores(formData.severity)}
                      onChange={handleAdditionalFieldChange}
                      onBlur={() => handleErrors('score')}
                      error={(formData.score === '' && formErrors.score) || !!scoreError}
                      helperText={(formData.score === '' && formErrors.score) ? 'Field is required' : scoreError || ''}
                      disabled
                    />
                  </FormControl>
                </div>
              )}
              {additionalFieldsEnabled && (
                <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pa-md">
                  <FormControl fullWidth>
                    <TextField
                      multiline
                      maxRows={4}
                      name="impact"
                      label="Impact"
                      value={formData.impact}
                      onChange={handleAdditionalFieldChange}
                      onBlur={() => handleErrors('impact')}
                      error={formData.impact === '' && formErrors.impact}
                      helperText={formData.impact === '' && formErrors.impact ? 'Field is required' : ''}
                    />
                  </FormControl>
                </div>
              )}
              {additionalFieldsEnabled && (
                <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pa-md">
                  <FormControl fullWidth>
                    <TextField
                      multiline
                      maxRows={4}
                      name="remediation"
                      label="Remediation"
                      value={formData.remediation}
                      onChange={handleAdditionalFieldChange}
                      onBlur={() => handleErrors('remediation')}
                      error={formData.remediation === '' && formErrors.remediation}
                      helperText={formData.remediation === '' && formErrors.remediation ? 'Field is required' : ''}
                    />
                  </FormControl>
                </div>
              )}
               {additionalFieldsEnabled && (
                <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md">
                  <FormControl fullWidth>
                    <TextField
                     multiline
                      name="ThreatActor"
                      label="ThreatActor"
                      maxRows={4}
                      value={formData.ThreatActor}
                      onChange={handleAdditionalFieldChange}
                      onBlur={() => handleErrors('threatactor')}
                      error={(formData.ThreatActor === '' && formErrors.ThreatActor) }
                      helperText={(formData.ThreatActor === '' && formErrors.ThreatActor) ? 'Field is required' : ''}
                    />
                  </FormControl>
                </div>
              )}
               
               {additionalFieldsEnabled && (
                <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md">
                  <FormControl fullWidth>
                    <TextField
                     multiline
                      name="Tactics"
                      label="Tactics"
                      maxRows={4}
                      value={formData.Tactics}
                      onChange={handleAdditionalFieldChange}
                      onBlur={() => handleErrors('Tactics')}
                      error={(formData.Tactics === '' && formErrors.Tactics) }
                      helperText={(formData.Tactics === '' && formErrors.Tactics) ? 'Field is required' : ''}
                    />
                  </FormControl>
                </div>
              )}
               {additionalFieldsEnabled && (
                <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pa-md">
                  <FormControl fullWidth>
                    <TextField
                     multiline
                      name="ThreatActorDescription"
                      label="ThreatActorDescription"
                      maxRows={4}
                      value={formData.ThreatActorDescription}
                      onChange={handleAdditionalFieldChange}
                      onBlur={() => handleErrors('ThreatActorDescription')}
                      error={(formData.ThreatActorDescription === '' && formErrors.ThreatActorDescription) }
                      helperText={(formData.ThreatActorDescription === '' && formErrors.ThreatActorDescription) ? 'Field is required' : ''}
                    />
                  </FormControl>
                </div>
              )}
              {additionalFieldsEnabled && (
                <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pa-md">
                  <Editor  onChange={setEvidence} />
                </div>
              )} 
              {additionalFieldsEnabled && (
                <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pa-md">
                  <Button variant="contained" onClick={handleAddField}>
                    Add More Fields
                  </Button>
                  <div className="note-content">
                    <p>
                      <strong>Note:</strong> You can use fields like <em>Threat Actors</em> as <code>ThreatActor</code>, 
                      <em> Tactics</em> as <code>Tactics</code>, and <em>Threat Actor Description</em> as 
                      <code>ThreatActorDescription</code>.
                    </p>
                  </div>
                </div>
              )}
              {dynamicFields.map((field, index) => (
                  <div className="row" key={index}>
                    <div className="col-lg-5 col-md-5 col-sm-12 col-xs-12 pa-md ">
                      <TextField
                        fullWidth
                        label="Field Name"
                        name="fieldName"
                        value={field.fieldName}
                        onChange={(e) => handleDynamicFieldChange(index, 'fieldName', e.target.value)}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md">
                      <TextField
                        fullWidth
                        label="Field Value"
                        name="fieldValue"
                        value={field.fieldValue}
                        onChange={(e) => handleDynamicFieldChange(index, 'fieldValue', e.target.value)}
                      />
                    </div>
                    <div className="col-lg-1 col-md-1 col-sm-6 col-xs-6 pa-md">
                      <IconButton onClick={() => handleRemoveField(index)}>
                        <DeleteIcon sx={{ color: 'red' }} />
                      </IconButton>
                    </div>
                  </div>
              ))}
              {additionalFieldsEnabled && (
                <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pa-md" style={{ textAlign: 'center' }}>
                  <Button variant="contained" color="primary" onClick={handleSubmit}>
                    Submit
                  </Button>
                </div>
              )}
            </div>
          </Card>
          </div>
          <div className="col-lg-4 col-md-4 col-sm-12 col-xs-12">
           {reportDate && report?.domain!==null &&  <UploadedVulnerabilities domain={report?.domain} date={reportDate} />}
        </div>
        </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default UploadVulnerabilities;